import {
  Button,
  Center,
  CloseButton,
  Dialog,
  Field,
  Fieldset,
  HStack,
  Input,
  Portal,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { toaster } from 'provider/src/module/chakra-ui/components/ui/toaster.tsx';
import { useTranslate } from '@tolgee/react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import ApplicationErrorView from '@packages/charka-common-ui/graphql/error/application-error-view';
import { useDeletePageRule } from '../logic/use-delete-page-rule';
import { usePageRule } from '../logic/use-page-rule';
import { useUpdatePageRule } from '../logic/use-update-page-rule';
import SectionMultipleSelect from './section-multiple-select';
import PrinterDeviceSelection from './printer-device-multiple-device-selection';

interface FormValues {
  name: string;
  menuSectionIds: string[];
  printerDeviceId?: string;
}

export default function UpdatePageRule(props: { id: string }) {
  const { t } = useTranslate();

  const { data, loading, error } = usePageRule(props.id);
  const [update, { loading: updateLoading }] = useUpdatePageRule();
  const [deletePageRule, { loading: deleteLoading, error: deleteError }] = useDeletePageRule();

  const [open, setOpen] = useState(false);
  const {
    watch,
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm<FormValues>();

  useEffect(() => {
    reset({
      name: data?.pageRule.name,
      menuSectionIds: data?.pageRule.allowedSections.map((item) => item.menuSectionId),
      printerDeviceId: data?.pageRule.printerDeviceId ?? undefined,
    });
  }, [data?.pageRule.allowedSections, data?.pageRule.name, data?.pageRule.printerDeviceId, reset]);

  const onSubmit = () => {
    update({
      variables: {
        id: props.id,
        input: {
          name: watch('name'),
          printerDeviceId: watch('printerDeviceId') == '' ? null : watch('printerDeviceId'),
          allowedSections: watch('menuSectionIds').map((item) => ({
            menuSectionId: item,
          })),
        },
      },
    })
      .then(() => {
        toaster.create({
          description: t('page-rule.update.alert', 'Update page rule successful'),
          type: 'info',
        });
        setOpen(false);
      })
      .catch(() => {
        toaster.create({
          description: t('page-rule.update.alert.failed', 'Update page rule failed'),
          type: 'error',
        });
      });
  };

  const onDelete = () => {
    deletePageRule({
      variables: {
        id: props.id,
      },
    })
      .then(() => {
        toaster.create({
          description: t('page-rule.delete.alert', 'Delete page rule successful'),
          type: 'info',
        });
        setOpen(false);
      })
      .catch(() => {
        toaster.create({
          description: t('page-rule.delete.alert.failed', 'Delete page rule failed'),
          type: 'error',
        });
      });
  };

  if (loading) {
    return (
      <Center>
        <Spinner />
      </Center>
    );
  }

  const FieldSet = (
    <Fieldset.Root size="lg" maxW="md">
      <Fieldset.Content>
        <Field.Root>
          <Field.Label>{t('print-rule.create.name', 'Name')}</Field.Label>
          <Input
            {...register('name', { required: t('print-rule.create.name-required', 'Name is required') })}
            name="name"
          />

          {errors.name && (
            <Text color="red.500" fontSize="sm">
              {errors.name.message}
            </Text>
          )}
        </Field.Root>

        <Field.Root>
          <Field.Label>{t('print-rule.create.section', 'Allowed sections')}</Field.Label>
          <SectionMultipleSelect
            allowedSections={watch('menuSectionIds')}
            onChange={(item: string[]) => {
              setValue('menuSectionIds', item);
            }}
          />
        </Field.Root>

        <Field.Root>
          <Field.Label>{t('print-rule.create.printer', 'Printer devices')}</Field.Label>
          <PrinterDeviceSelection
            printerDeviceId={watch('printerDeviceId')}
            onChange={(printerId: string) => {
              setValue('printerDeviceId', printerId);
            }}
          />
        </Field.Root>
      </Fieldset.Content>
    </Fieldset.Root>
  );

  return (
    <Dialog.Root
      lazyMount
      open={open}
      onOpenChange={(e) => {
        setOpen(e.open);
      }}
      placement={'center'}
    >
      <Dialog.Trigger asChild>
        <Button variant={'surface'} p={2} bgColor={'white'}>
          {t('page-rule.button.edit-print-rule', 'Edit')}
        </Button>
      </Dialog.Trigger>
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>
              <Dialog.Title fontSize={'xl'}>{t('page-rule.dialog.update.title', 'Update Page Rule')}</Dialog.Title>
            </Dialog.Header>

            <ApplicationErrorView error={error ?? deleteError} />

            <Dialog.Body>{FieldSet}</Dialog.Body>
            <Dialog.Footer>
              <HStack justifyContent={'space-between'} w={'full'}>
                <Button
                  loading={deleteLoading}
                  onClick={onDelete}
                  p={2}
                  variant={'surface'}
                  bgColor={'red.500'}
                  color={'white'}
                >
                  {t('common.button.delete')}
                </Button>
                <HStack>
                  <Dialog.ActionTrigger asChild>
                    <Button variant="surface" p={2}>
                      {t('common.button.cancel', 'Cancel')}
                    </Button>
                  </Dialog.ActionTrigger>
                  <Button
                    loading={updateLoading}
                    onClick={() => {
                      void handleSubmit(onSubmit)();
                    }}
                    p={2}
                    bg={'gray.700'}
                    variant={'surface'}
                    color={'white'}
                    type="submit"
                  >
                    {t('common.button.update')}
                  </Button>
                </HStack>
              </HStack>
            </Dialog.Footer>
            <Dialog.CloseTrigger asChild>
              <CloseButton size="sm" />
            </Dialog.CloseTrigger>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
}
